const CASE_BLOG = 'business-nursery-case-studies';
const VIEW = 'case-study-return-map';

export async function loadCaseStudyIndex(endpoint, options = {}) {
  const baseURL = options.baseURL || globalThis.location.href;
  const fetchPage = options.fetchImpl || globalThis.fetch.bind(globalThis);
  const firstURL = new URL(endpoint, baseURL);
  const origin = new URL(baseURL).origin;
  if (firstURL.origin !== origin || firstURL.searchParams.get('view') !== VIEW) {
    throw new Error('Invalid case study endpoint');
  }
  const casePrefix = `${firstURL.pathname.replace(/\/$/, '')}/`;
  const index = new Map();
  const seenCases = new Set();
  let totalPages;
  let page = 1;
  while (page !== null) {
    const url = new URL(firstURL);
    url.searchParams.set('page', String(page));
    const response = await fetchPage(url.href, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: options.signal
    });
    if (!response.ok) throw new Error('Case study lookup unavailable');
    const data = JSON.parse(await response.text());
    if (data.version !== 1 || data.blog !== CASE_BLOG || data.page !== page ||
        !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 ||
        !Array.isArray(data.entries) || data.entries.length > 250) {
      throw new Error('Invalid case study index');
    }
    const pages = Math.max(1, data.pages);
    if (totalPages !== undefined && totalPages !== pages) {
      throw new Error('Case study index changed during lookup');
    }
    totalPages = pages;
    const expectedNext = page < pages ? page + 1 : null;
    if (page > pages || data.next !== expectedNext) {
      throw new Error('Invalid case study pagination');
    }
    for (const entry of data.entries) {
      if (typeof entry.case_id !== 'string' || !/^\d+$/.test(entry.case_id) ||
          typeof entry.interview_id !== 'string' ||
          (entry.interview_id !== '' && !/^\d+$/.test(entry.interview_id)) ||
          typeof entry.case_url !== 'string' || seenCases.has(entry.case_id)) {
        throw new Error('Invalid case study reference');
      }
      seenCases.add(entry.case_id);
      const target = new URL(entry.case_url, firstURL);
      if (target.origin !== origin || !target.pathname.startsWith(casePrefix) ||
          target.pathname.length <= casePrefix.length || target.search || target.hash) {
        throw new Error('Invalid case study destination');
      }
      if (!entry.interview_id) continue;
      index.set(entry.interview_id, index.has(entry.interview_id) ? null : target.pathname);
    }
    page = expectedNext;
  }
  return index;
}

export function updateReturnLink(link, index) {
  const target = index.get(link.dataset.interviewId);
  const destination = target || link.dataset.fallbackUrl;
  const label = target ? link.dataset.caseStudyLabel : link.dataset.fallbackLabel;
  link.setAttribute('href', destination);
  link.textContent = label;
  link.dataset.caseStudyState = target ? 'matched' : 'fallback';
}

function initializeReturnLinks() {
  const links = [...document.querySelectorAll('a[data-case-study-return]')];
  if (!links.length) return;
  const requests = new Map();
  function activate(link) {
    if (link.dataset.caseStudyState !== 'pending') return;
    link.dataset.caseStudyState = 'loading';
    const endpoint = link.dataset.caseStudyEndpoint;
    if (!requests.has(endpoint)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      requests.set(endpoint, loadCaseStudyIndex(endpoint, { signal: controller.signal })
        .finally(() => clearTimeout(timeout)));
    }
    requests.get(endpoint)
      .then(index => updateReturnLink(link, index))
      .catch(() => { link.dataset.caseStudyState = 'unavailable'; });
  }
  if ('IntersectionObserver' in globalThis) {
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          activate(entry.target);
        }
      }
    }, { rootMargin: '1200px 0px' });
    for (const link of links) observer.observe(link);
  } else {
    for (const link of links) activate(link);
  }
  for (const link of links) {
    link.addEventListener('focus', () => activate(link), { once: true });
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeReturnLinks, { once: true });
  } else {
    initializeReturnLinks();
  }
}
